import React, { useMemo } from 'react';
import { navigationUiStateResource } from '@atlassian/jira-router-resources-navigation-ui-state/src/index.tsx';
import { useResource, createRouterSelector } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createContainer } from '@atlassian/react-sweet-state';
import type { RightSidebarState, LayoutState } from '../../../../common/types.tsx';
import { actions, type LayoutActions } from '../../actions/index.tsx';

import { layoutStore, DEFAULT_LEFT_SIDEBAR_WIDTH } from '../../store/index.tsx';
import type { LayoutManagerProps, LayoutContainerProps } from './types.tsx';

const LayoutContainer = createContainer<LayoutState, LayoutActions, LayoutContainerProps>(
	layoutStore,
	{
		displayName: 'JiraLayoutContainer',
		onInit:
			() =>
			({ dispatch }, { leftSidebar, rightSidebar, isChromeless }) => {
				dispatch(actions.setIsChromeless(isChromeless));
				dispatch(actions.setLeftSidebarState(leftSidebar));
				dispatch(actions.setRightSidebarState(rightSidebar));
			},
		onUpdate:
			() =>
			({ dispatch }, { isChromeless }) => {
				dispatch(actions.setIsChromeless(isChromeless));
			},
	},
);

const DEFFAULT_RIGHT_SIDEBAR_STATE: RightSidebarState = {
	stackState: [],
};

const useIsChromeless = createRouterSelector(
	(state) =>
		state.route.layout?.isChromeless === true ||
		state.route.entryPoint?.()?.toString() === 'ChromelessEntryPoint',
);

export const LayoutManager = ({ children }: LayoutManagerProps) => {
	const { data } = useResource(navigationUiStateResource);
	const isChromeless = useIsChromeless();

	const isLeftSidebarCollapsed = data?.leftSidebar?.isCollapsed ?? false;
	const leftSidebarWidth = data?.leftSidebar?.width ?? DEFAULT_LEFT_SIDEBAR_WIDTH;
	const leftSidebar = useMemo(
		() => ({
			isVisible: true,
			isCollapsed: isLeftSidebarCollapsed,
			width: leftSidebarWidth,
			isFlyoutOpen: false,
		}),
		[isLeftSidebarCollapsed, leftSidebarWidth],
	);

	return (
		<LayoutContainer
			isGlobal
			isChromeless={isChromeless}
			leftSidebar={leftSidebar}
			rightSidebar={DEFFAULT_RIGHT_SIDEBAR_STATE}
		>
			{children}
		</LayoutContainer>
	);
};
